import React, { useId, useRef, useState, useMemo } from 'react';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  createColumnHelper,
  ColumnFiltersState,
  getFilteredRowModel,
  getSortedRowModel,
  Row,
  VisibilityState,
} from '@tanstack/react-table';

import Button from 'design-system/src/components/Button/Button';
import type { NotificationRow } from '../../api';
import {
  sortOfficeNames,
  stringsToDropdownOptions,
  getDropdownInitialValueFromSearchParams,
  getInitialMultiselectFilterValueFromCookie,
  getInitialTableColumnFilterValueFromCookie,
  updateTableColumnFilterValueCookie,
  formatStandardDateLocaleString,
  formatStandardDate,
} from '../../utils';
import { useFetchPortalTableFilters } from '../useFetchPortalTableFilters';
import { TableMultiselectFilter } from '../TableDropdownFilter/TableDropdownFilter';
import type {
  MultiselectAutocompleteOption,
  MultiselectAutocompleteHandle,
} from 'design-system/src/components/MultiselectAutocomplete/MultiselectAutocomplete';
import { AuthContext } from '../../auth';

import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  ClickableRow,
  FilterBar,
  FilterBarRow,
  FilterBarRowSection,
  SearchInput,
  Table,
  TableCell,
  TableContainer,
  TableHeader,
  HeaderSortingIndicator,
  TableTabList,
  ResultCountContainer,
  ResultCount,
  DividerPipe,
  NoPaddingCheckbox,
  DEFAULT_COLUMN_SIZE,
  DATE_COLUMN_SIZE,
} from '../Table';
import { Typography } from 'design-system/src/components/Typography/Typography';
import styled from 'styled-components';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { client } from '../../api';
import SaveFiltersModal from '../SaveFiltersModal/SaveFiltersModal';
import { SaveFiltersButton } from '../SaveFiltersButton';
import { areStringArraysEqual } from '../../utils';
import { toast } from 'react-hot-toast';
import { Toast, StandardErrorToast } from 'design-system/src/components/Toast/Toast';
import { AxiosError } from 'axios';
import * as Sentry from '@sentry/react';
import { notificationsQueryKey } from '../../queries';
import TableNullState, { TableNullStateType } from '../TableNullState/TableNullState';
import { useTableNullState } from '../../hooks/useTableNullState';
import FormattedCell from '../FormattedCell';
import { useDefaultFilters } from '../../hooks/useDefaultFilters';

const columnHelper = createColumnHelper<NotificationRow>();

interface NotificationsTableProps {
  data: NotificationRow[];
}

const taskDateSortingFn = (a: Row<NotificationRow>, b: Row<NotificationRow>, columnId: string) => {
  return new Date(b.getValue(columnId)).getTime() - new Date(a.getValue(columnId)).getTime();
};

const taskPatientSortingFn = (a: Row<NotificationRow>, b: Row<NotificationRow>, columnId: string) => {
  const aValue = a.getValue(columnId) as { name: string; dob: string };
  const bValue = b.getValue(columnId) as { name: string; dob: string };
  return bValue.name.localeCompare(aValue.name);
};

const taskTypeSortingFn = (a: Row<NotificationRow>, b: Row<NotificationRow>, columnId: string) => {
  const aValue = a.getValue(columnId) as { type: string; drugDescription: string; context: string };
  const bValue = b.getValue(columnId) as { type: string; drugDescription: string; context: string };
  return bValue.type.localeCompare(aValue.type);
};

const taskTableMultiSelectFilter = (row: Row<NotificationRow>, columnId: string, filterValue: string[]) => {
  if (!filterValue.length) {
    // No filters are applied; return all rows
    return true;
  }

  // @ts-expect-error columnId will be one of the column heading keys
  return filterValue.includes(row.original[columnId]);
};

const TopAlignedTableCell = styled(TableCell)`
  vertical-align: top;
`;

const getDefaultNullStateType = (status: string) => {
  if (status === 'completed') {
    return TableNullStateType.NO_NOTIFICATIONS_COMPLETED;
  } else {
    return TableNullStateType.NO_NOTIFICATIONS_OPEN;
  }
};

const NotificationsTable: React.FC<NotificationsTableProps> = ({ data }) => {
  const navigate = useNavigate();
  const { notificationId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const authContext = React.useContext(AuthContext);
  const userEmail = authContext.user?.email;
  const { data: filterOptions } = useFetchPortalTableFilters();
  const [selectedNotifications, setSelectedNotifications] = React.useState<string[]>([]);
  const isTrainingMode = import.meta.env.VITE_TRAINING_MODE === 'true';
  const queryClient = useQueryClient();
  const statusTabId = useId();
  const statusPanelId = useId();

  // Refs for TableMultiselectFilter components
  const officeFilterRef = useRef<MultiselectAutocompleteHandle>(null);
  const prescriberFilterRef = useRef<MultiselectAutocompleteHandle>(null);
  const typeFilterRef = useRef<MultiselectAutocompleteHandle>(null);

  const { defaultPrescriberNames } = useDefaultFilters();

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const selectableRows = table.getRowModel().rows.filter((row) => row.original.status !== 'completed');
      const selectableIds = selectableRows.map((row) => row.original.id);
      setSelectedNotifications(selectableIds);
    } else {
      setSelectedNotifications([]);
    }
  };

  const closeNotificationsMutation = useMutation<{ success: boolean }, AxiosError<{ error: string }>>({
    mutationFn: async () => {
      if (isTrainingMode) {
        // Simulating a successful response without sending the request
        return Promise.resolve({ success: true });
      }

      return client
        .post('portal-notifications/close', { notifications: selectedNotifications })
        .then((res) => res.data);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: notificationsQueryKey() });
      toast.custom(() => <Toast variant="success">Notifications marked as read</Toast>);
      setSelectedNotifications([]);
    },
    onError: async (error) => {
      await queryClient.invalidateQueries({ queryKey: notificationsQueryKey() });
      toast.custom(() => <StandardErrorToast />);
      Sentry.captureException(error);
      setSelectedNotifications([]);
    },
  });

  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([
    { id: 'status', value: searchParams.get('status') || 'open' },
    {
      id: 'office_name',
      value: getInitialTableColumnFilterValueFromCookie('notifications', 'office_name', userEmail),
    },
    {
      id: 'prescriber_name',
      value: defaultPrescriberNames || [],
    },
  ]);

  const currentPrescriberFilter = (columnFilters.find((f) => f.id === 'prescriber_name')?.value as string[]) || [];
  const currentPrescriberFilterNpis = currentPrescriberFilter
    .map((name) => {
      const prescriber = filterOptions?.prescribers?.find((p) => p.description === name);
      return prescriber?.value;
    })
    .filter((npi): npi is string => npi !== undefined);
  const showSaveButton = useMemo(() => {
    // Convert defaultPrescriberNames to NPIs for comparison
    const defaultPrescriberNpis =
      defaultPrescriberNames
        ?.map((name) => {
          const prescriber = filterOptions?.prescribers?.find((p) => p.description === name);
          return prescriber?.value;
        })
        .filter((npi): npi is string => npi !== undefined) || [];

    return !areStringArraysEqual(currentPrescriberFilterNpis, defaultPrescriberNpis);
  }, [currentPrescriberFilterNpis, defaultPrescriberNames, filterOptions?.prescribers]);
  const [showSaveModal, setShowSaveModal] = useState(false);

  const typeStrings: string[] = [];
  data.forEach((task) => {
    if (task.task_type) {
      typeStrings.push(task.task_type);
    }
  });

  const handleCheckboxChange = (notificationId: string) => {
    setSelectedNotifications((prev) => {
      if (prev.includes(notificationId)) {
        return prev.filter((id) => id !== notificationId);
      } else {
        return [...prev, notificationId];
      }
    });
  };

  const handleMarkAsComplete = () => {
    closeNotificationsMutation.mutate();
  };

  const columns = [
    columnHelper.accessor('id', {
      id: 'selection',
      header: () => {
        const selectableRows = table.getRowModel().rows.filter((row) => row.original.status !== 'completed');
        const allSelected =
          selectableRows.length > 0 && selectableRows.every((row) => selectedNotifications.includes(row.original.id));
        const isDisabled = table.getColumn('status')?.getFilterValue() === 'completed';

        return (
          <NoPaddingCheckbox
            checked={allSelected}
            onChange={(e) => handleSelectAll(e.target.checked)}
            disabled={isDisabled}
            onClick={(e) => e.stopPropagation()}
          />
        );
      },
      cell: (info) => {
        return (
          <NoPaddingCheckbox
            checked={selectedNotifications.includes(info.getValue())}
            onChange={(e) => {
              e.stopPropagation();
              handleCheckboxChange(info.getValue());
            }}
            onClick={(e) => e.stopPropagation()}
            disabled={table.getColumn('status')?.getFilterValue() === 'completed'}
          />
        );
      },
      enableSorting: false,
      enableGlobalFilter: false,
      size: 42,
    }),
    columnHelper.accessor('created_at', {
      header: 'Date',
      sortingFn: taskDateSortingFn,
      cell: (info) => formatStandardDateLocaleString(info.getValue()),
      size: DATE_COLUMN_SIZE,
    }),
    columnHelper.accessor(
      (row) => {
        return { name: row.patient_name, dob: row.patient_dob };
      },
      {
        id: 'patient_info',
        header: 'Patient info',
        sortingFn: taskPatientSortingFn,
        cell: (info) => (
          <FormattedCell
            id={info.row.original.id}
            title={info.getValue().name}
            subtitle={`DOB: ${formatStandardDate(info.getValue().dob)}`}
          />
        ),
        size: DEFAULT_COLUMN_SIZE,
      },
    ),
    columnHelper.accessor(
      (row) => {
        return { type: row.task_type, context: row.extra_context, drugDescription: row.drug_description };
      },
      {
        id: 'notification',
        header: 'Notification',
        sortingFn: taskTypeSortingFn,
        cell: (info) => (
          <FormattedCell
            id={info.row.original.id}
            title={info.getValue().type}
            subtitle={info.getValue().drugDescription}
            body={info.getValue().context}
          />
        ),
        filterFn: (row, _, filterValue) => {
          if (!filterValue.length) return true;
          return filterValue.includes(row.original.task_type);
        },
        size: 600,
      },
    ),
    columnHelper.accessor('prescriber_name', {
      header: 'Prescriber',
      filterFn: taskTableMultiSelectFilter,
      size: DEFAULT_COLUMN_SIZE,
    }),

    // these columns are only used for filtering, not displaying
    columnHelper.accessor('status', {
      header: 'Status',
      filterFn: (row, _, filterValue) => {
        if (filterValue == 'open') {
          return row.original.status == 'queued';
        } else if (filterValue == 'completed') {
          return row.original.status == 'completed';
        }
        return true;
      },
      enableGlobalFilter: false,
    }),
    columnHelper.accessor('office_name', {
      header: 'Office',
      enableGlobalFilter: false,
      filterFn: taskTableMultiSelectFilter,
    }),
  ];

  const [searchValue, setSearchValue] = useState('');
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({
    // These columns are always hidden
    status: false,
    office_name: false,
    // The selection column is only visible on the Open tab because you can't select completed notifications
    selection: searchParams.get('status') !== 'completed',
  });

  const table = useReactTable({
    data,
    columns,
    state: {
      columnFilters,
      globalFilter: searchValue,
      columnVisibility,
    },
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    enableGlobalFilter: true,
    // Explicitly define the global filter function so that it works despite the formatted cells
    globalFilterFn: (row, _, filterValue) => {
      if (!filterValue || typeof filterValue !== 'string') return true;

      const searchTerm = filterValue.toLowerCase();
      if (row.original.patient_name?.toLowerCase().includes(searchTerm)) return true;
      if (row.original.prescriber_name?.toLowerCase().includes(searchTerm)) return true;
      if (row.original.drug_description?.toLowerCase().includes(searchTerm)) return true;
      if (row.original.task_type?.toLowerCase().includes(searchTerm)) return true;
      if (row.original.extra_context?.toLowerCase().includes(searchTerm)) return true;

      const createdAtStr = formatStandardDateLocaleString(row.original.created_at);
      if (createdAtStr.toLowerCase().includes(searchTerm)) return true;

      const dobStr = formatStandardDate(row.original.patient_dob);
      if (dobStr.toLowerCase().includes(searchTerm)) return true;

      return false;
    },
    initialState: {
      columnVisibility,
    },
  });

  const setStatus = React.useCallback(
    (status: string, e?: React.MouseEvent) => {
      if (e) {
        e.stopPropagation();
      }
      setSearchParams({ status });
      table.getColumn('status')!.setFilterValue(status);
    },
    [setSearchParams, table],
  );

  const handleRowClick = (row: any) => {
    navigate(`/rxs/${row.rx_id}`);
  };

  const nullStateType = useTableNullState({
    columnFilters,
    table,
    filterIds: ['office_name', 'prescriber_name', 'notification'],
    defaultNullStateType: getDefaultNullStateType(table.getColumn('status')?.getFilterValue() as string),
    globalFilter: searchValue,
  });

  const clearAllFilters = React.useCallback(() => {
    // Clear all column filters except status (which is controlled by URL)
    const statusFilter = columnFilters.find((f) => f.id === 'status');
    setColumnFilters(statusFilter ? [statusFilter] : []);

    // Clear global filter
    setSearchValue('');
    table.setGlobalFilter('');

    // Clear cookies for saved filter values
    updateTableColumnFilterValueCookie([], 'notifications', 'office_name', userEmail);

    // Clear TableMultiselectFilter refs
    officeFilterRef.current?.clear();
    prescriberFilterRef.current?.clear();
    typeFilterRef.current?.clear();
  }, [columnFilters, table, userEmail, setSearchValue]);

  const resultCountSection = (
    <FilterBarRowSection>
      <ResultCountContainer>
        <ResultCount>
          {table.getRowModel().rows?.length} {table.getRowModel().rows?.length === 1 ? 'result' : 'results'}
        </ResultCount>
      </ResultCountContainer>
    </FilterBarRowSection>
  );

  return (
    <TableContainer>
      <FilterBar>
        <FilterBarRow>
          <TableTabList
            tabs={[
              { label: 'Open', value: 'open' },
              { label: 'Completed', value: 'completed' },
            ]}
            selectedValue={(table.getColumn('status')!.getFilterValue() as string) || 'open'}
            onSelect={(value) => {
              setStatus(value);
              setColumnVisibility((prev) => ({
                ...prev,
                selection: value === 'open',
              }));
            }}
            tabListId={statusTabId}
            tabPanelId={statusPanelId}
            ariaLabel="Filter notifications by status"
          />
        </FilterBarRow>

        <FilterBarRow $reorderOnSmall>
          {!!selectedNotifications.length ? (
            <>
              <Button size="sm" onClick={handleMarkAsComplete} disabled={closeNotificationsMutation.isPending}>
                Mark as complete
              </Button>
              <Button size="sm" variant="tertiary" onClick={() => setSelectedNotifications([])}>
                Deselect
              </Button>
              <DividerPipe />
              <Typography>{selectedNotifications.length} selected</Typography>
            </>
          ) : (
            <>
              <SearchInput
                label="Search by patient name or DOB"
                hideLabel
                value={searchValue}
                onChange={(e) => {
                  const value = (e.target as HTMLInputElement).value;
                  setSearchValue(value);
                  table.setGlobalFilter(value);
                }}
              />
              <TableMultiselectFilter
                options={filterOptions?.offices || []}
                label={'Office'}
                initialValue={getInitialMultiselectFilterValueFromCookie('notifications', 'office_name', userEmail)}
                sortFn={sortOfficeNames}
                onChange={(_, value: MultiselectAutocompleteOption[]) => {
                  const selectedValuesList = value.map((valueItem) => valueItem.value);
                  table.getColumn('office_name')!.setFilterValue(selectedValuesList);
                  updateTableColumnFilterValueCookie(value, 'notifications', 'office_name', userEmail);
                }}
                ref={officeFilterRef}
              />
              <TableMultiselectFilter
                options={filterOptions?.prescribers || []}
                label={'Prescriber'}
                initialValue={
                  getDropdownInitialValueFromSearchParams(
                    authContext.user?.default_filters,
                    '',
                    'providers',
                    filterOptions?.prescribers,
                  ) as MultiselectAutocompleteOption[]
                }
                onChange={(_, value: MultiselectAutocompleteOption[]) => {
                  const selectedNamesList = value.map((valueItem) => valueItem.description);
                  table.getColumn('prescriber_name')!.setFilterValue(selectedNamesList);
                }}
                ref={prescriberFilterRef}
              />
              <TableMultiselectFilter
                options={stringsToDropdownOptions(typeStrings)}
                label={'Type'}
                onChange={(_, value: MultiselectAutocompleteOption[]) => {
                  const selectedValuesList = value.map((valueItem) => valueItem.value);
                  table.getColumn('notification')!.setFilterValue(selectedValuesList);
                }}
                ref={typeFilterRef}
              />
              <SaveFiltersButton $visible={showSaveButton} onClick={() => setShowSaveModal(true)} />
            </>
          )}

          {resultCountSection}
        </FilterBarRow>
      </FilterBar>

      <Table role="tabpanel" id={statusPanelId} aria-labelledby={statusTabId}>
        <thead>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                return (
                  <TableHeader
                    key={header.id}
                    onClick={header.column.getCanSort() ? () => header.column.toggleSorting() : undefined}
                    $width={header.column.getSize().toString()}
                    $sortable={header.column.getCanSort()}
                  >
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                    {header.column.getCanSort() && (
                      <HeaderSortingIndicator
                        isSorted={header.column.getIsSorted()}
                        sortDirection={header.column.getIsSorted()}
                      />
                    )}
                  </TableHeader>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row) => {
              const isSelected = selectedNotifications.includes(row.original.id);

              return (
                <ClickableRow
                  $selected={row.original.id === notificationId || isSelected}
                  key={row.original.id}
                  onClick={() => handleRowClick(row.original)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      handleRowClick(row.original);
                    }
                  }}
                >
                  {row.getVisibleCells().map((cell) => {
                    return (
                      <TopAlignedTableCell key={cell.id} $width={cell.column.getSize().toString()}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TopAlignedTableCell>
                    );
                  })}
                </ClickableRow>
              );
            })
          ) : (
            <tr>
              <TableCell colSpan={columns.length}>
                <TableNullState nullStateType={nullStateType} onClearFilters={clearAllFilters} />
              </TableCell>
            </tr>
          )}
        </tbody>
      </Table>
      {showSaveModal && (
        <SaveFiltersModal
          onClose={() => setShowSaveModal(false)}
          filters={[
            { label: 'Prescriber', displayValues: currentPrescriberFilter, saveValues: currentPrescriberFilterNpis },
          ]}
        />
      )}
    </TableContainer>
  );
};

export default NotificationsTable;
